/**
 * MobileNavigationAnalyticsFunction.ts
 * Common analytics functions for mobile bottom navigation components
 */

import {
  dlEventConstants,
  headerConstants,
} from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";

/**
 * Handle mobile bottom navigation menu icon click analytics
 * Logs click event for the menu icon in the mobile bottom navigation
 */
export const handleBottomNavigationMenuClickDL = (
  cta_text: string = headerConstants.MOBILE_NAVIGATION.CTA_TEXT.MENU,
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_ICONS,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.CARD_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle mobile bottom navigation icon click analytics
 * Logs click event for any icon in the mobile bottom navigation
 */
export const handleBottomNavigationIconClickDL = (
  iconName: string,
  iconIndex: number,
  destinationUrl?: string,
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_ICONS,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.CARD_CLICK,
    cta_text: iconName,
    icon_index: iconIndex,
    destination_url: destinationUrl || "",
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    icon_index: String(data.icon_index), // Convert to string for CleverTap
    destination_url: data.destination_url,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle mobile hamburger menu close button click analytics
 * Logs click event for the close button in the mobile hamburger menu
 */
export const handleHamburgerMenuCloseClickDL = (
  cta_text: string = headerConstants.MOBILE_NAVIGATION.CTA_TEXT
    .BOTTOM_NAVIGATION_ICONS,
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_ICONS,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.ICON_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle mobile hamburger menu card click analytics
 * Logs click event for the menu cards in the mobile hamburger menu
 */
export const handleHamburgerMenuCardClickDL = (
  cta_text: string,
  index: number,
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.CARD_CLICK,
    cta_text,
    card_index: index,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    card_index: String(data.card_index), // Convert to string for CleverTap
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle cross sell links click analytics
 * Logs click event for the cross sell links in the mobile footer
 */
export const handleCrossSellLinkClickDL = (cta_text: string) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.LINK_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle hamburger menu link click analytics
 * Logs click event for the main menu links in the hamburger menu
 * @param cta_text The text of the clicked link
 * @param menuSection Optional section identifier within the hamburger menu
 */
export const handleHamburgerMenuLinkClickDL = (
  cta_text: string,
  menuSection: string = "main_menu",
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.LINK_CLICK,
    cta_text,
    menu_section: menuSection, // Added to differentiate from cross-sell links
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    menu_section: data.menu_section,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle social media icon click analytics
 * Logs click event for the social media icons in the mobile footer
 */
export const handleSocialMediaIconClickDL = (cta_text: string) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component:
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.SOCIAL_ICON_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};
